import { memo, type CSSProperties } from 'react'

/**
 * Renders an icon from `ionicons/icons`.
 *
 * Those exports are SVG data URLs. Outline variants depend on classes inside
 * the SVG (`ionicon-fill-none`, `ionicon-stroke-width`) that Ionic styled
 * from its shadow root, so the markup is inlined rather than used as an
 * image or mask — ui.css supplies the same rules.
 */

const cache = new Map<string, string>()

function svgMarkup (src: string): string {
  const hit = cache.get(src)
  if (hit !== undefined) return hit
  let markup = ''
  if (src.startsWith('data:image/svg+xml')) {
    const body = src.slice(src.indexOf(',') + 1)
    try {
      markup = body.includes('%3C') || body.includes('%3c') ? decodeURIComponent(body) : body
    } catch {
      markup = body
    }
    // Only keep the <svg> element itself.
    const start = markup.indexOf('<svg')
    markup = start >= 0 ? markup.slice(start) : ''
    markup = markup.replace(/<script[\s\S]*?<\/script>/gi, '')
  }
  cache.set(src, markup)
  return markup
}

export interface IconProps {
  icon: string
  size?: 'small' | 'large'
  color?: string
  className?: string
  style?: CSSProperties
  /** Accessible label; when omitted the icon is decorative. */
  label?: string
  /** Accepted for parity with ion-icon; layout is decided by the parent. */
  slot?: string
  title?: string
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void
}

export const Icon = memo(function Icon ({ icon, size, color, className, style, label, title, onClick }: IconProps) {
  const classes = ['icon']
  if (size) classes.push(`icon--${size}`)
  if (color) classes.push(`text--${color}`)
  if (className) classes.push(className)
  return (
    <span
      className={classes.join(' ')}
      style={style}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      title={title}
      onClick={onClick}
      dangerouslySetInnerHTML={{ __html: svgMarkup(icon) }}
    />
  )
})
